/**
 * GifCodecPool — a few codec workers over ONE file, presented as the runner
 * GifCodecSearch expects ({encode, parallel, proxyShare}).
 *
 * Every worker reads the File itself (posting a File copies no bytes), builds
 * its own GifCodec.Session, and encodes one job at a time. A palette is
 * computed once per colour count (every frame size uses the full-resolution
 * one) and handed to every worker with its jobs, so all encodes share it (and
 * the decode pass it costs).
 *
 * Size: one worker per spare core (at most 3; for a big file one per two cores, at most 6),
 * capped by memory — each worker
 * holds the file bytes plus ~156 bytes per pixel of frame (encoder state ~57,
 * the search-in-depth arrays ~46, decoding ~16 — counted from the typed arrays —
 * and up to nine kept decoder states, a frame of RGBA each: where the runs of a
 * long clip's sample and the slices of a parallel encode start)
 * plus a proxy of up to ~48 MB. Three stay the ceiling for an ordinary file — for the
 * memory they hold, and because its search is over in a minute. A file over BIG_BYTES is
 * minutes of whole-file encodes: a 56 MB clip took 154 s on three workers and 86 s on six
 * (same result), so on a desktop it gets up to six where the cores and the memory are there.
 * Cancelling = terminate(): every job promise rejects with AbortError.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.GifCodecPool = api;
}(typeof self !== 'undefined' ? self : typeof globalThis !== 'undefined' ? globalThis : null, function () {
    'use strict';

    function abortError() {
        var e;
        try { e = new DOMException('cancelled', 'AbortError'); } catch (_) { e = new Error('cancelled'); e.name = 'AbortError'; }
        return e;
    }

    var BIG_BYTES = 32e6;       // a file whose search is minutes of whole-file encodes (see header)
    function workerBytes(bytes, px) { return bytes + px * 156 + 64e6; }   // file + encoder and decoder state + proxy
    var PHONE_BIG = 800e6;      // all the workers of a big file on a phone, together — what three workers of a
                                // file just under BIG_BYTES already hold there. A quarter of an 8 GB phone would be
                                // 2 GB of workers in a tab whose own ceiling nobody reports
    function isPhone(env) {
        if (env && env.phone != null) return !!env.phone;
        try {
            return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') || !!(navigator.userAgentData && navigator.userAgentData.mobile);
        } catch (_) { return false; }
    }
    function budgetBytes(env, bytes) {
        var mb = (env && env.memBudget) || (typeof self !== 'undefined' ? self.__memBudget : null);
        var b = !mb ? 512e6 : mb.constrained() ? 300e6 : mb.gb() * 1e9 * 0.25;
        return bytes > BIG_BYTES && isPhone(env) ? Math.min(b, PHONE_BIG) : b;
    }

    /**
     * How many workers this device should run for a file of `bytes` with `px`
     * pixels per frame. Memory comes from the page's one owner of that question,
     * window.__memBudget (navigator.deviceMemory does not exist in WebKit, and an
     * iPhone tab dies at ~500-700 MB whatever the phone has) — `env` may pass it in.
     */
    function poolSize(bytes, px, env) {
        env = env || {};
        var cores = env.cores || (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 2;
        var byMem = Math.max(1, Math.floor(budgetBytes(env, bytes) / workerBytes(bytes, px)));
        var few = Math.min(3, cores - 1);
        // logical cores come in pairs on most desktops: one worker per pair leaves the page its share
        var byCores = bytes > BIG_BYTES && !isPhone(env) ? Math.min(6, Math.max(few, Math.floor(cores / 2))) : few;
        return Math.max(1, Math.min(byCores, byMem));
    }

    /**
     * Can this device hold even ONE worker for such a file? poolSize() answers «at least
     * one» whatever the file is; a page that has another route for a file it cannot hold
     * (the big-file pre-shrink) asks this first.
     */
    function fits(bytes, px, env) {
        return workerBytes(bytes, px) <= budgetBytes(env || {}, bytes);
    }

    /**
     * @param {File|Blob} file
     * @param {{workerUrl:string, size?:number}} opts   workerUrl from asset-router->worker()
     * @returns {Promise<runner>}
     */
    /** concatenate parallel slices (first has the header) + trailer; average their scores */
    function joinSlices(parts) {
        var n = 1, i, s2 = 0, de = 0, w2 = 0, wd = 0;
        for (i = 0; i < parts.length; i++) n += parts[i].bytes.length;
        var out = new Uint8Array(n), o = 0;
        for (i = 0; i < parts.length; i++) {
            out.set(parts[i].bytes, o); o += parts[i].bytes.length;
            var q = parts[i].quality, w = q && q.n ? q.n : 0;
            if (q && q.s2 != null && w) { s2 += q.s2 * w; w2 += w; }
            if (q && q.de != null && w) { de += q.de * w; wd += w; }
        }
        out[o] = 0x3B;
        return {
            bytes: out, size: out.length,
            quality: { s2: w2 ? s2 / w2 : null, de: wd ? de / wd : null },
            width: parts[0].width, height: parts[0].height,
            ms: Math.max.apply(null, parts.map(function (p) { return p.ms || 0; })),
            slices: parts.length
        };
    }

    function create(file, opts) {
        var size = opts.size || 1, workers = [], queue = [], seq = 0, dead = false;
        var palettes = {}, palettePending = {}, readyInfo = null, proxyShare = 0.1, sample = null;

        function spawn() {
            return new Promise(function (resolve, reject) {
                var w = new Worker(opts.workerUrl);
                var slot = { w: w, busy: null };
                w.onmessage = function (ev) {
                    var m = ev.data || {};
                    if (m.type === 'ready') { readyInfo = readyInfo || m.info; proxyShare = m.proxyShare || proxyShare; sample = sample || m.sample || null; resolve(slot); return; }
                    var job = slot.busy;
                    if (m.type === 'progress') { if (job && job.onProgress) job.onProgress(m.frac); return; }
                    if (m.type === 'error' && !job) { reject(new Error(m.message)); return; }
                    if (!job || m.id !== job.id) return;
                    slot.busy = null;
                    if (m.type === 'error') { var err = new Error(m.message); err.workerStack = m.stack; job.reject(err); }
                    else {
                        if (m.bytes) m.bytes = new Uint8Array(m.bytes);
                        job.resolve(m);
                    }
                    pump();
                };
                w.onerror = function (ev) {
                    var e = new Error((ev && ev.message) || 'codec worker failed');
                    if (slot.busy) { var j = slot.busy; slot.busy = null; j.reject(e); }
                    else reject(e);
                    if (ev && ev.preventDefault) ev.preventDefault();
                };
                w.postMessage({ type: 'init', file: file, proxy: !opts.scoreOnly, slices: size });
                workers.push(slot);
            });
        }

        function pump() {
            if (dead) return;
            for (var i = 0; i < workers.length && queue.length; i++) {
                var s = workers[i];
                if (s.busy || !s.ready) continue;
                var job = queue.shift();
                s.busy = job;
                s.w.postMessage(job.msg);
            }
        }

        function submit(msg, onProgress) {
            if (dead) return Promise.reject(abortError());
            return new Promise(function (resolve, reject) {
                msg.id = ++seq;
                queue.push({ id: msg.id, msg: msg, resolve: resolve, reject: reject, onProgress: onProgress });
                pump();
            });
        }

        function palette(K) {
            var key = String(K || 255);
            if (palettes[key]) return Promise.resolve(palettes[key]);
            if (!palettePending[key]) {
                palettePending[key] = submit({ type: 'palette', K: K || 255 }).then(function (m) { palettes[key] = m.palette; return m.palette; });
            }
            return palettePending[key];
        }

        var runner = {
            parallel: size,
            get proxyShare() { return proxyShare; },
            get info() { return readyInfo; },
            /** a long clip's stand-in for the search's real encodes (GifCodec samplePlan), or null */
            get sample() { return sample; },
            encode: function (params, o) {
                o = o || {};
                return palette(params.K).then(function (pal) {
                    // The sample: every run is a job of its own (any worker — each holds the decoder
                    // state its runs start from). Sizes add up; a run is scored on one frame.
                    if (o.sample && sample) {
                        return Promise.all(sample.runs.map(function (r) {
                            return submit({ type: 'encode', params: params, proxy: false, keep: false, palette: pal, from: r[0], to: r[1], warm: true });
                        })).then(function (parts) {
                            var bytes = 0, s2 = 0, n2 = 0;
                            parts.forEach(function (p) { bytes += p.size; if (p.quality && p.quality.s2 != null) { s2 += p.quality.s2; n2++; } });
                            return { size: bytes, quality: { s2: n2 ? s2 / n2 : null, de: null }, width: parts[0].width, height: parts[0].height,
                                ms: Math.max.apply(null, parts.map(function (p) { return p.ms || 0; })) };
                        });
                    }
                    var ready = workers.filter(function (w) { return w.ready; }).length;
                    var frames = readyInfo ? readyInfo.frames : 0, px = readyInfo ? readyInfo.width * readyInfo.height : 0;
                    // A full encode is cut into time slices, one per worker, each starting on a full
                    // frame (measured +0-1.2 % on real clips). Small files are fast anyway and would
                    // pay relatively more for the extra frames (+6.5 % on a 30-frame clip).
                    var slices = (!o.proxy && o.keep && frames * px >= 6e6) ? Math.min(ready, Math.floor(frames / 16)) : 1;
                    if (slices < 2) return submit({ type: 'encode', params: params, proxy: !!o.proxy, keep: !!o.keep, palette: pal }, o.onProgress);
                    var jobs = [], done = new Array(slices);
                    for (var i = 0; i < slices; i++) {
                        (function (k) {
                            var from = Math.round(k * frames / slices), to = Math.round((k + 1) * frames / slices);
                            jobs.push(submit({ type: 'encode', params: params, proxy: false, keep: true, palette: pal, from: from, to: to }, function (f) {
                                done[k] = f;
                                if (o.onProgress) { var sum = 0; for (var j = 0; j < slices; j++) sum += done[j] || 0; o.onProgress(sum / slices); }
                            }));
                        })(i);
                    }
                    return Promise.all(jobs).then(joinSlices);
                });
            },
            /** quality of a finished GIF made from this file (GifCodec.Session.score) */
            score: function (bytes, o) {
                var u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
                // a copy goes to the worker: the page keeps its own bytes for the download
                var buf = u.byteOffset === 0 && u.byteLength === u.buffer.byteLength ? u.buffer.slice(0) : u.slice().buffer;
                return submit({ type: 'score', bytes: buf, de: !(o && o.de === false) }).then(function (m) { return m.quality; });
            },
            terminate: function () {
                if (dead) return;
                dead = true;
                workers.forEach(function (s) { try { s.w.terminate(); } catch (_) {} if (s.busy) s.busy.reject(abortError()); });
                queue.splice(0).forEach(function (j) { j.reject(abortError()); });
            }
        };

        var starts = [];
        for (var i = 0; i < size; i++) starts.push(spawn().then(function (slot) { slot.ready = true; pump(); return slot; }));
        // the pool is usable as soon as ONE worker is up; the rest join as they finish reading.
        // Only when every worker failed does the pool fail.
        return new Promise(function (resolve, reject) {
            var failed = 0;
            starts.forEach(function (p) {
                p.then(function () { resolve(runner); }, function (e) {
                    if (++failed === starts.length) { runner.terminate(); reject(e); }
                });
            });
        });
    }

    return { create: create, poolSize: poolSize, fits: fits };
}));
